/**
 * チャットメッセージアイテムコンポーネント
 * UserMessage / AIMessage を切り替える
 */

import { useEffect } from 'react';
import { Box, Button } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ChatMessage } from '@/types';
import { UserMessage } from './UserMessage';
import { AIMessage } from './AIMessage';
import { useTypewriter } from '@/hooks/useTypewriter';

interface ChatMessageItemProps {
  message: ChatMessage;
  onStreamingComplete?: (messageId: string) => void;
}

export const ChatMessageItem = ({ message, onStreamingComplete }: ChatMessageItemProps) => {
  const { t } = useTranslation();
  const {
    id,
    isUser,
    isLoading,
    isError,
    isStreaming,
    user_input,
    ai_response,
    attachedFiles,
    generatedImage,
    sources,
    event,
    age_group,
  } = message;

  const streamingEnabled = !isUser && !isLoading && !!isStreaming && !!ai_response;

  const { displayedText, isTyping, skip } = useTypewriter({
    fullText: ai_response ?? '',
    enabled: streamingEnabled,
  });

  useEffect(() => {
    if (streamingEnabled && !isTyping) {
      onStreamingComplete?.(id);
    }
  }, [streamingEnabled, isTyping, id, onStreamingComplete]);

  if (isUser) {
    return <UserMessage text={user_input} attachedFiles={attachedFiles} />;
  }

  const displayText = streamingEnabled ? displayedText : ai_response;
  const isTypewriterActive = streamingEnabled && isTyping;

  return (
    <>
      <AIMessage
        text={displayText}
        isError={isError}
        isLoading={isLoading}
        generatedImage={generatedImage}
        sources={sources}
        event={event}
        ageGroup={age_group}
      />
      {isTypewriterActive && (
        <Box mt={2} display="flex" justifyContent="flex-start">
          <Button
            variant="cancel"
            size="sm"
            onClick={skip}
            aria-label={t('chat.skipTypewriter.ariaLabel')}
          >
            {t('chat.skipTypewriter.label')}
          </Button>
        </Box>
      )}
    </>
  );
};
